/**
 * Shared fetch for /api/v1/agent/* with Bearer, workspace header, and token refresh.
 */

import { getBearerAuthHeaders } from "@/lib/authSession";
import { fetchWithTimeout } from "@/lib/fetchWithTimeout";
import { fetchWithAuthRetry } from "@/lib/authedRequest";
import { inboxWorkspaceHeaders } from "@/lib/inboxLiveConfig";

/**
 * @param {string} path - e.g. "/api/v1/agent/ticket-email/config"
 * @param {RequestInit & { workspaceId?: string | null, body?: unknown }} options
 */
export async function agentApiFetch(path, options = {}) {
  const { workspaceId, body, method = "GET", headers: extraHeaders, ...rest } = options;

  const buildRequest = () => {
    const url = new URL(path, (import.meta.env?.VITE_API_BASE_URL || window.location.origin));
    const headers = {
      Accept: "application/json",
      ...getBearerAuthHeaders(),
      ...inboxWorkspaceHeaders(workspaceId),
      ...(extraHeaders || {}),
    };
    if (body !== undefined && !headers["Content-Type"]) {
      headers["Content-Type"] = "application/json";
    }
    return fetchWithTimeout(url.toString(), {
      method,
      credentials: "include",
      headers,
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      ...rest,
    });
  };

  const res = await fetchWithAuthRetry(buildRequest);
  const text = await res.text();
  let data = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = { raw: text };
    }
  }

  if (!res.ok) {
    const msg = data?.detail?.error || data?.detail?.message || data?.message || data?.error;
    const detail =
      typeof msg === "string"
        ? msg
        : typeof data?.detail === "string"
          ? data.detail
          : JSON.stringify(msg || data?.detail || res.statusText || "Request failed");
    const err = new Error(detail);
    err.status = res.status;
    err.data = data;
    throw err;
  }

  if (res.status === 204) return null;
  return data;
}
